import { Controller } from '@hotwired/stimulus';

const LEFT_BORDER = 0;
const PADDING_LEFT_CAPTION = 24;

let grabScrollPosition = {
    left: 0,
    x: 0,
};

export default class extends Controller {
    static targets = [
        'tableContainer',
        'table',
        'moveLeftArrow',
        'moveRightArrow',
        'firstColumn',
        'lastColumn',
        'scrollableTableData',
        'lastColumnTableBody',
        'firstColumnTableBody',
        'tableHeader',
        'tableProductImage',
        'tableCaption',
        'tableProductOutletPrice',
    ];

    connect = () => {
        this.#scrollToTop();
        this.#showHideScrollButtons();
    };

    moveScrollLeft = () => {
        this.#moveToThePreviousTableCell();
        this.#showHideScrollButtons();
    };

    moveScrollRight = () => {
        this.#moveToTheNextTableCell();
        this.#showHideScrollButtons();
    };

    scroll = () => {
        this.#showHideScrollButtons();

        if (this.hasLastColumnTableBodyTarget) {
            this.#setWidthLastHeaderColumn();
        }

        this.#syncHeaderAndContentTableScrolls();
    };

    #moveToThePreviousTableCell() {
        this.tableHeaderTarget.scrollLeft -= this.#getTableCellWidth();
        this.tableContainerTarget.scrollLeft -= this.#getTableCellWidth();
    }

    #moveToTheNextTableCell() {
        this.tableHeaderTarget.scrollLeft += this.#getTableCellWidth();
        this.tableContainerTarget.scrollLeft += this.#getTableCellWidth();
    }

    #getTableCellWidth = () => this.tableTarget.querySelector('th:nth-child(2)').offsetWidth;

    #showHideScrollButtons() {
        const scrollRightButtonHide = this.#isScrollAtRightBorder();
        const scrollLeftButtonHide = this.#isScrollAtLeftBorder();

        if (this.hasMoveLeftArrowTarget) {
            this.moveLeftArrowTarget.classList.toggle('invisible', scrollLeftButtonHide);
        }

        if (this.hasMoveRightArrowTarget) {
            this.moveRightArrowTarget.classList.toggle('invisible', scrollRightButtonHide);
        }

        this.#evaluateFirstColumnSticky(scrollLeftButtonHide);
        this.#evaluateLastColumnSticky(scrollRightButtonHide);
        this.#evaluateGrabHand(!scrollRightButtonHide || !scrollLeftButtonHide);
    }

    #isScrollAtRightBorder = () => {
        const scrollWidth = Math.trunc(this.tableTarget.clientWidth) - Math.trunc(this.tableContainerTarget.clientWidth);
        return (Math.trunc(this.tableContainerTarget.scrollLeft) >= scrollWidth);
    };

    #isScrollAtLeftBorder = () => (Math.trunc(this.tableContainerTarget.scrollLeft) === Math.trunc(LEFT_BORDER));

    #evaluateFirstColumnSticky = (scrollLeftButtonHide) => {
        if (this.hasFirstColumnTarget) {
            this.firstColumnTargets.forEach((column) => {
                column.classList.toggle('sticky', !scrollLeftButtonHide);
                column.classList.toggle('sticky-left', !scrollLeftButtonHide);
            });
        }
    };

    #evaluateLastColumnSticky = (scrollRightButtonHide) => {
        if (this.hasLastColumnTarget) {
            this.lastColumnTargets.forEach((column) => {
                column.classList.toggle('sticky', !scrollRightButtonHide);
                column.classList.toggle('sticky-right', !scrollRightButtonHide);
            });
        }
    };

    mouseDownHandler = (event) => {
        if (!this.#isScrollAtLeftBorder() || !this.#isScrollAtRightBorder()) {
            this.scrollableTableDataTargets.forEach((scrollableTableDataTarget) => {
                scrollableTableDataTarget.classList.add('grabbing');
            });

            grabScrollPosition = {
                left: this.tableContainerTarget.scrollLeft,
                x: event.clientX,
            };

            document.addEventListener('mousemove', this.#mouseMoveHandler);
            document.addEventListener('mouseup', this.#mouseUpHandler);
        }
    };

    #mouseMoveHandler = (event) => {
        const distanceX = event.clientX - grabScrollPosition.x;
        this.tableHeaderTarget.scrollLeft = grabScrollPosition.left - distanceX;
        this.tableContainerTarget.scrollLeft = grabScrollPosition.left - distanceX;
    };

    #mouseUpHandler = () => {
        document.removeEventListener('mousemove', this.#mouseMoveHandler);
        document.removeEventListener('mouseup', this.#mouseUpHandler);

        this.scrollableTableDataTargets.forEach((scrollableTableDataTarget) => {
            scrollableTableDataTarget.classList.remove('grabbing');
        });
    };

    #scrollToTop = () => window.scrollTo(0, 0);

    #evaluateGrabHand = (isScrollable) => {
        document.querySelectorAll('.scrollable')
            .forEach((scrollable) => scrollable.classList.toggle('no-grab', !isScrollable));
    };

    #setWidthHeaderColumns() {
        this.#setWidthFirstHeaderColumn();
        this.#setWidthLastHeaderColumn();
    }

    #setWidthFirstHeaderColumn() {
        const firstBodyColumnWidth = this.firstColumnTableBodyTarget.getBoundingClientRect().width;
        const firstColumnHeader = this.tableHeaderTarget.querySelector('th:first-child');
        firstColumnHeader.style.minWidth = `${firstBodyColumnWidth}px`;
        firstColumnHeader.style.width = `${firstBodyColumnWidth}px`;
        firstColumnHeader.style.maxWidth = `${firstBodyColumnWidth}px`;
    }

    // firstColumnTableBodyTargetConnected() {
    //     this.#setWidthFirstHeaderColumn();
    // }
    //
    // lastColumnTableBodyTargetConnected() {
    //     this.#setWidthLastHeaderColumn();
    // }

    #setWidthLastHeaderColumn() {
        const lastBodyColumnWidth = this.lastColumnTableBodyTarget.getBoundingClientRect().width;
        const lastColumnHeader = this.tableHeaderTarget.querySelector('th:last-child');
        lastColumnHeader.style.minWidth = `${lastBodyColumnWidth}px`;
        lastColumnHeader.style.width = `${lastBodyColumnWidth}px`;
    }

    // setStickyHeader() {
    //     const headerMainRectangle = document.querySelector('.header-main')
    //         .getBoundingClientRect();
    //     this.#toggleTableHeaderClasses(headerMainRectangle);
    //     this.tableHeaderTarget.style.top = `${headerMainRectangle.bottom - 2}px`;
    // }

    // #toggleTableHeaderClasses(headerMainRectangle) {
    //     const needsStickyHeader = this.tableHeaderTarget.getBoundingClientRect().top <= headerMainRectangle.bottom;
    //
    //     if (this.hasTableHeaderTarget) {
    //         this.tableHeaderTarget.classList.toggle('sticky-header--active', needsStickyHeader);
    //     }
    //
    //     if (this.hasTableCaptionTarget) {
    //         this.tableCaptionTarget.classList.toggle('d-none', !needsStickyHeader);
    //     }
    // }

    #syncHeaderAndContentTableScrolls() {
        if (this.hasTableHeaderTarget) {
            this.tableHeaderTarget.scrollLeft = this.tableContainerTarget.scrollLeft;
        }

        if (this.hasTableCaptionTarget) {
            this.tableCaptionTarget.style.paddingLeft = `${this.tableContainerTarget.scrollLeft + PADDING_LEFT_CAPTION}px`;
        }
    }
}
